/**
 * <ProtectedRoute> — gates a route subtree on (1) an authenticated session and (2) the route's
 * requiredCapabilities (ALL must be held). Unauthenticated -> redirect to /login (preserving the
 * intended destination). Authenticated but missing a capability -> render ForbiddenState (NOT a
 * redirect — the user is logged in, they just lack the right; the 5-state DoD applies).
 */
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ForbiddenState } from "@/ui";
import { useAuthStore } from "./store";

interface ProtectedRouteProps {
  /** Capabilities ALL required to view (empty = any authenticated user). */
  requiredCapabilities?: ReadonlyArray<string>;
  children: ReactNode;
}

export function ProtectedRoute({ requiredCapabilities = [], children }: ProtectedRouteProps) {
  const location = useLocation();
  const status = useAuthStore((s) => s.status);
  const allowed = useAuthStore((s) =>
    requiredCapabilities.every((c) => s.capabilities.has(c)),
  );

  if (status !== "authenticated") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!allowed) {
    return <ForbiddenState requiredCapabilities={requiredCapabilities} />;
  }
  return <>{children}</>;
}
